import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bell, CheckCheck, Mail, MailOpen, X } from 'lucide-react';
import { toast } from 'sonner';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [tab, setTab] = useState('unread');
  const [loading, setLoading] = useState(true);
  const [viewNotif, setViewNotif] = useState(null);

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      const list = res.data.notifications || res.data || [];
      setNotifications(list);
    } catch { toast.error('فشل تحميل الإشعارات'); }
    finally { setLoading(false); }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
    } catch { toast.error('فشل التحديث'); }
  };

  const openNotification = async (n) => {
    setViewNotif(n);
    if (!n.is_read) {
      await markAsRead(n.id);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('تم تحديد الكل كمقروء');
    } catch { toast.error('فشل التحديث'); }
  };

  const unread = notifications.filter((n) => !n.is_read);
  const read = notifications.filter((n) => n.is_read);
  const display = tab === 'unread' ? unread : read;

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div></div>;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-accent mb-1">الوارد</h1>
          <p className="text-muted-foreground">{unread.length} إشعار غير مقروء</p>
        </div>
        {unread.length > 0 && (
          <Button onClick={markAllAsRead} variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="mark-all-read">
            <CheckCheck className="w-5 h-5 ml-2" /> تحديد الكل كمقروء
          </Button>
        )}
      </div>

      <div className="flex gap-3" data-testid="notification-tabs">
        <button onClick={() => setTab('unread')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${tab === 'unread' ? 'bg-primary text-white shadow-lg' : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-primary/30'}`}
          data-testid="tab-unread">
          <Mail className="w-4 h-4" /> غير المقروء
          {unread.length > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${tab === 'unread' ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}`}>{unread.length}</span>}
        </button>
        <button onClick={() => setTab('read')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${tab === 'read' ? 'bg-gray-700 text-white shadow-lg' : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-gray-400'}`}
          data-testid="tab-read">
          <MailOpen className="w-4 h-4" /> المقروء
          {read.length > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${tab === 'read' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>{read.length}</span>}
        </button>
      </div>

      {display.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-16 h-16 text-muted mx-auto mb-4 opacity-30" />
          <p className="text-lg text-muted-foreground">{tab === 'unread' ? 'لا توجد إشعارات جديدة' : 'لا توجد إشعارات مقروءة'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {display.map((n) => (
            <Card key={n.id} onClick={() => openNotification(n)}
              className={`p-5 transition-all hover:shadow-md cursor-pointer ${!n.is_read ? 'border-r-4 border-r-primary bg-primary/[0.02]' : ''}`}
              data-testid={`notification-${n.id}`}>
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-full shrink-0 mt-1 ${!n.is_read ? 'bg-primary/10' : 'bg-gray-100'}`}>
                  <Bell className={`w-5 h-5 ${!n.is_read ? 'text-primary' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-accent">{n.title}</h3>
                    {!n.is_read && <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">جديد</span>}
                  </div>
                  <p className="text-muted-foreground text-sm line-clamp-2">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">{new Date(n.created_at).toLocaleString('ar')}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Notification Detail Modal */}
      {viewNotif && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewNotif(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-bold text-accent">{viewNotif.title}</h2>
              <button onClick={() => setViewNotif(null)} data-testid="close-notif-modal"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{new Date(viewNotif.created_at).toLocaleString('ar')}</p>
            {viewNotif.image_url && (
              <img src={viewNotif.image_url} alt="" className="w-full max-h-64 object-cover rounded-xl mb-4" />
            )}
            <p className="text-accent leading-relaxed whitespace-pre-wrap">{viewNotif.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
